import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import TrustSection from '../components/TrustSection';
import UserJourney from '../components/UserJourney';
import AnnouncementBar from '../components/AnnouncementBar';
import { PWAInstallButton } from '../components/PWAInstallButton';
import { useAuth } from '../context/AuthContext';
import { getHoldings } from '../services/portfolioHoldingService';
import { fetchStockPrice, fetchUSDtoINR } from '../services/stockPriceService';
import { formatCurrency } from '../utils/calculatorEngine';
import { applyFixedInstrumentValues } from '../utils/fixedInstrumentCalc';

const Home = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showDeletedToast, setShowDeletedToast] = useState(false);
  const { user } = useAuth();
  const [portfolioPulse, setPortfolioPulse] = useState(null);
  const [pulseLoading, setPulseLoading] = useState(false);

  // Handle deletion success message
  useEffect(() => {
    if (searchParams.get('deleted') === 'true') {
      setShowDeletedToast(true);
      searchParams.delete('deleted');
      setSearchParams(searchParams, { replace: true });
      const timer = setTimeout(() => setShowDeletedToast(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [searchParams, setSearchParams]);

  // Load portfolio pulse for authenticated users
  const loadPortfolioPulse = useCallback(async () => {
    if (!user) {
      setPortfolioPulse(null);
      return;
    }
    setPulseLoading(true);
    try {
      const result = await getHoldings();
      if (result.success && result.data && result.data.length > 0) {
        // Normalize holdings the same way as PortfolioDashboard
        const holdings = result.data.map((h) => {
          const quantity = parseFloat(h.quantity) || parseFloat(h.units) || 0;
          const avgCost = parseFloat(h.avg_buy_price) || parseFloat(h.avg_cost) || 0;
          const currentPrice = parseFloat(h.current_price) || avgCost;
          const currentValue = parseFloat(h.current_value) || quantity * currentPrice;
          const investedValue = parseFloat(h.invested_amount) || parseFloat(h.invested_value) || 0;
          return {
            ...h,
            current_value: currentValue,
            invested_value: investedValue,
            quantity,
            current_price: currentPrice,
          };
        });

        const stockCount = holdings.filter((h) =>
          ['STOCK', 'STOCK_INDIAN'].includes(h.asset_type)
        ).length;
        const mfCount = holdings.filter((h) => ['MF', 'MUTUAL_FUND'].includes(h.asset_type)).length;

        const computePulse = (list) => {
          const tv = list.reduce((sum, h) => sum + (h.current_value || 0), 0);
          const ti = list.reduce((sum, h) => sum + (h.invested_value || 0), 0);
          return {
            totalValue: tv,
            totalInvested: ti,
            totalGain: tv - ti,
            gainPercent: ti > 0 ? ((tv - ti) / ti) * 100 : 0,
            holdingCount: list.length,
            stockCount,
            mfCount,
          };
        };

        // Refresh live stock prices before showing pulse (avoids value flash)
        let finalHoldings = holdings;
        const stockHoldings = holdings.filter(
          (h) => ['STOCK_INDIAN', 'STOCK_US'].includes(h.asset_type) && h.quantity > 0 && h.symbol
        );
        if (stockHoldings.length > 0) {
          try {
            const exchangeRate = await fetchUSDtoINR();
            const updates = (
              await Promise.all(
                stockHoldings.map(async (h) => {
                  const livePrice = await fetchStockPrice(h.symbol, h.asset_type);
                  if (livePrice > 0) {
                    const value =
                      h.asset_type === 'STOCK_US'
                        ? h.quantity * livePrice * exchangeRate
                        : h.quantity * livePrice;
                    return { id: h.id, current_value: value };
                  }
                  return null;
                })
              )
            ).filter(Boolean);

            if (updates.length > 0) {
              finalHoldings = holdings.map((h) => {
                const u = updates.find((u) => u.id === h.id);
                return u ? { ...h, current_value: u.current_value } : h;
              });
            }
          } catch (priceErr) {
            // Live price fetch failed — use DB values as fallback
          }
        }

        setPortfolioPulse(computePulse(applyFixedInstrumentValues(finalHoldings)));
      }
    } catch (e) {
      // silent
    } finally {
      setPulseLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadPortfolioPulse();
  }, [loadPortfolioPulse]);

  return (
    <div className="home-page">
      <AnnouncementBar />
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 1rem' }}>
        <PWAInstallButton />
      </div>
      {showDeletedToast && (
        <div className="deletion-toast">
          <span>Your data has been deleted successfully.</span>
          <button onClick={() => setShowDeletedToast(false)}>&times;</button>
        </div>
      )}
      <SEO
        title="FINNCAL: Life on Your Terms | Portfolio & Freedom"
        description="India's all-in-one personal finance platform. Track your portfolio, run X-Ray health checks, plan your financial freedom and grow family wealth."
        url="https://finncal.com/"
      />

      {/* Portfolio Pulse for returning authenticated users */}
      {user && !pulseLoading && portfolioPulse && (
        <section
          style={{
            maxWidth: '900px',
            margin: '0 auto 1.5rem',
            padding: '0 1rem',
          }}
        >
          <div
            style={{
              background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              borderRadius: '16px',
              padding: '1.5rem 2rem',
              color: 'white',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
            }}
          >
            <div>
              <div style={{ fontSize: '0.8rem', opacity: 0.7, marginBottom: '0.25rem' }}>
                Your Total Wealth
              </div>
              <div style={{ fontSize: '2rem', fontWeight: '700', letterSpacing: '-0.5px' }}>
                {formatCurrency(portfolioPulse.totalValue)}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginTop: '0.5rem',
                  fontSize: '0.85rem',
                }}
              >
                <span
                  style={{
                    color: portfolioPulse.totalGain >= 0 ? '#4ade80' : '#f87171',
                    fontWeight: '600',
                  }}
                >
                  {portfolioPulse.totalGain >= 0 ? '+' : ''}
                  {formatCurrency(portfolioPulse.totalGain)} (
                  {portfolioPulse.gainPercent >= 0 ? '+' : ''}
                  {portfolioPulse.gainPercent.toFixed(1)}%)
                </span>
                <span style={{ opacity: 0.5 }}>|</span>
                <span style={{ opacity: 0.7 }}>
                  {portfolioPulse.holdingCount} holdings
                  {portfolioPulse.stockCount > 0 && portfolioPulse.mfCount > 0
                    ? ` (${portfolioPulse.stockCount} stocks, ${portfolioPulse.mfCount} MFs)`
                    : portfolioPulse.stockCount > 0
                      ? ` (${portfolioPulse.stockCount} stocks)`
                      : portfolioPulse.mfCount > 0
                        ? ` (${portfolioPulse.mfCount} mutual funds)`
                        : ''}
                </span>
              </div>
            </div>
            <Link
              to="/tools/portfolio"
              style={{
                padding: '0.625rem 1.25rem',
                background: 'rgba(255,255,255,0.15)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '8px',
                fontSize: '0.85rem',
                fontWeight: '500',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => (e.target.style.background = 'rgba(255,255,255,0.25)')}
              onMouseLeave={(e) => (e.target.style.background = 'rgba(255,255,255,0.15)')}
            >
              View Portfolio &rarr;
            </Link>
          </div>
        </section>
      )}

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <p className="hero-tagline">Join thousands of Indians taking control of their finances</p>
          <h1 className="hero-title">Your Complete Financial Picture. One Platform.</h1>
          <p className="hero-subtitle">
            Track your portfolio, check its health, plan your freedom.
          </p>

          {/* Primary + Secondary CTA */}
          <div className="hero-cta-group">
            <Link to="/tools/portfolio" className="hero-cta-primary">
              Get Started Free &rarr;
            </Link>
            <Link to="/tools/quick-check" className="hero-cta-secondary">
              or Calculate Your Freedom Number &rarr;
            </Link>
          </div>
        </div>
      </section>

      {/* AI Assistant Strip — prominent, right below hero */}
      <section
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
          padding: '2.5rem 1.5rem',
          marginBottom: '2rem',
        }}
      >
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          {/* Top row: badge + headline + sub */}
          <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
            <span
              style={{
                display: 'inline-block',
                background: 'rgba(37,99,235,0.85)',
                color: 'white',
                fontSize: '0.7rem',
                fontWeight: '700',
                letterSpacing: '0.1em',
                padding: '0.25rem 0.8rem',
                borderRadius: '999px',
                marginBottom: '0.9rem',
              }}
            >
              AI-POWERED · KNOWS YOUR ACTUAL DATA
            </span>
            <h2
              style={{
                fontSize: 'clamp(1.4rem, 3vw, 2rem)',
                fontWeight: '700',
                color: 'white',
                margin: '0 0 0.6rem',
                lineHeight: 1.25,
              }}
            >
              Stop guessing. Get answers based on your real numbers.
            </h2>
            <p style={{ color: '#94a3b8', fontSize: '0.95rem', margin: 0 }}>
              Upload your portfolio · Complete your plan · Ask the AI
            </p>
          </div>

          {/* Question chips */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '0.75rem',
              marginBottom: '1.75rem',
            }}
          >
            {[
              { icon: '🏠', q: 'I have ₹10L extra — prepay home loan or invest in equity?' },
              { icon: '🛡️', q: 'Do I have adequate life & health insurance for my family?' },
              { icon: '📈', q: 'Am I on track for my Freedom Number? What SIP do I need?' },
              { icon: '⚖️', q: 'Is my portfolio allocation right for my age and goals?' },
            ].map(({ icon, q }) => (
              <button
                key={q}
                onClick={() => window.dispatchEvent(new CustomEvent('open-chatbot'))}
                style={{
                  background: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '12px',
                  padding: '0.9rem 1rem',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.6rem',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.2s, border-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.13)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.35)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.07)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
                }}
              >
                <span style={{ fontSize: '1.2rem', flexShrink: 0, lineHeight: 1 }}>{icon}</span>
                <span style={{ fontSize: '0.83rem', color: '#e2e8f0', lineHeight: 1.5 }}>{q}</span>
              </button>
            ))}
          </div>

          {/* CTA */}
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('open-chatbot'))}
              style={{
                background: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                padding: '0.875rem 2.25rem',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(37,99,235,0.45)',
                transition: 'background 0.2s, box-shadow 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#1d4ed8';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(37,99,235,0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#2563eb';
                e.currentTarget.style.boxShadow = '0 4px 14px rgba(37,99,235,0.45)';
              }}
            >
              Ask the AI Assistant &rarr;
            </button>
            <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.6rem' }}>
              1 free question · No credit card required
            </p>
          </div>
        </div>
      </section>

      {/* 4-Card Feature Section */}
      <section
        className="features-section"
        style={{ maxWidth: '1100px', margin: '0 auto 2rem', padding: '0 1rem' }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '1rem',
          }}
        >
          <Link to="/tools/portfolio" style={{ textDecoration: 'none' }}>
            <div className="feature-card">
              <div className="feature-card-icon">📊</div>
              <h3>Portfolio Tracker</h3>
              <p>
                Track all your stocks and mutual funds in one place. XIRR, P&amp;L, and live prices.
              </p>
            </div>
          </Link>
          <Link to="/tools/portfolio/xray" style={{ textDecoration: 'none' }}>
            <div className="feature-card">
              <div className="feature-card-icon">🔬</div>
              <h3>X-Ray</h3>
              <p>
                Detect fund overlap, hidden costs, and underperformers. Get a portfolio health
                score.
              </p>
            </div>
          </Link>
          <Link to="/tools/quick-check" style={{ textDecoration: 'none' }}>
            <div className="feature-card">
              <div className="feature-card-icon">🎯</div>
              <h3>Freedom Planning</h3>
              <p>
                Calculate your freedom number. Know exactly when you can stop working for money.
              </p>
            </div>
          </Link>
          <Link to="/tools/insurance-advisor" style={{ textDecoration: 'none' }}>
            <div className="feature-card">
              <div className="feature-card-icon">🛡️</div>
              <h3>Insurance Planner</h3>
              <p>
                Right cover at the right cost. Term, health, and family protection — all analysed.
              </p>
            </div>
          </Link>
          <Link to="/city-finder" style={{ textDecoration: 'none' }}>
            <div className="feature-card">
              <div className="feature-card-icon">🏙️</div>
              <h3>City Finder</h3>
              <p>See how your city compares — cost of living across 62 Indian cities. →</p>
            </div>
          </Link>
        </div>
      </section>

      {/* User Journey Guide */}
      <UserJourney />

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <h2 className="section-title">What Professionals Are Saying</h2>
        <div className="testimonials-grid">
          <div className="testimonial-card">
            <div className="testimonial-quote-icon">"</div>
            <p className="testimonial-text">
              FINNCAL gave me clarity I couldn't find anywhere else. The India-specific calculations
              and realistic assumptions made me realize I was closer to financial freedom than I
              thought. Finally, a tool built for how we actually plan retirement here.
            </p>
            <div className="testimonial-author">
              <span className="testimonial-name">Deepak Poddar</span>
              <span className="testimonial-designation">Finance Professional</span>
            </div>
          </div>

          <div className="testimonial-card">
            <div className="testimonial-quote-icon">"</div>
            <p className="testimonial-text">
              As someone who's evaluated many financial tools, FINNCAL stands out for its simplicity
              and depth. The stress testing feature showed me exactly how my portfolio would perform
              across market conditions. It's now part of my annual financial review.
            </p>
            <div className="testimonial-author">
              <span className="testimonial-name">Shantanu Naik</span>
              <span className="testimonial-designation">
                Sr. Vice President, Intellect Design Arena
              </span>
            </div>
          </div>

          <div className="testimonial-card">
            <div className="testimonial-quote-icon">"</div>
            <p className="testimonial-text">
              Most retirement calculators use Western assumptions that don't apply to India. FINNCAL
              gets it right — the inflation rates, the asset allocation, the safe withdrawal
              strategy. I recommend it to my clients who want a realistic picture of their
              retirement needs.
            </p>
            <div className="testimonial-author">
              <span className="testimonial-name">Santosh Vyas</span>
              <span className="testimonial-designation">Chartered Accountant</span>
            </div>
          </div>

          <div className="testimonial-card">
            <div className="testimonial-quote-icon">"</div>
            <p className="testimonial-text">
              As an academic, I've always believed in data-driven decisions — but my own retirement
              planning was based on guesswork. FINNCAL changed that completely. The comprehensive
              calculator showed me exactly where I stood, the insurance advisor revealed gaps I
              hadn't considered, and the city finder helped me compare post-retirement options I
              never knew existed. For the first time, I have a clear, confident financial plan — and
              I've already saved significantly by optimizing my corpus strategy. I recommend this to
              every colleague approaching retirement.
            </p>
            <div className="testimonial-author">
              <span className="testimonial-name">Prof. Sudipta Roy, Ph.D.</span>
              <span className="testimonial-designation">Professor & Dean, Assam University</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <TrustSection />
    </div>
  );
};

export default Home;
